import React, { useEffect, useState } from 'react';

const DeepLinkHandler = () => {
  const [isAttemptingDeepLink, setIsAttemptingDeepLink] = useState(false);

  // Function to detect if we're in a social media in-app browser
  const isInSocialMediaBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      userAgent.includes('instagram') ||
      userAgent.includes('fbav') ||
      userAgent.includes('fban') ||
      userAgent.includes('twitter') ||
      userAgent.includes('tiktok') ||
      userAgent.includes('snapchat') ||
      userAgent.includes('whatsapp') ||
      userAgent.includes('telegram') ||
      userAgent.includes('line') ||
      userAgent.includes('wechat') ||
      userAgent.includes('linkedin')
    );
  };

  // Function to detect if we're in a mobile browser
  const isMobileBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPad|iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  };

  // Function to get the appropriate app store URL
  const getAppStoreUrl = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'https://apps.apple.com/tr/app/eventle/id6474272088?l=tr';
    } else if (/android/i.test(userAgent)) {
      return 'https://play.google.com/store/apps/details?id=com.eventle.eventle&hl=tr';
    }
    return 'https://apps.apple.com/tr/app/eventle/id6474272088?l=tr'; // Default to iOS
  };

  // Function to get the appropriate deep link scheme
  const getDeepLinkScheme = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const currentPath = window.location.pathname + window.location.search;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return `eventlelink://${currentPath}`;
    } else if (/android/i.test(userAgent)) {
      return `intent://eventlelink${currentPath}#Intent;scheme=eventlelink;package=com.eventle.eventle;end`;
    }
    return `eventlelink://${currentPath}`; // Default
  };

  // Function to attempt opening the app
  const attemptOpenApp = () => {
    setIsAttemptingDeepLink(true);
    
    const appScheme = getDeepLinkScheme();
    const appStoreUrl = getAppStoreUrl();
    const isSocialMedia = isInSocialMediaBrowser();
    
    let timeout;
    let appOpened = false;
    let redirectAttempted = false;
    
    // Function to clear timeout and mark app as opened
    const clearTimeoutOnAppOpen = () => {
      if (!appOpened) {
        appOpened = true;
        clearTimeout(timeout);
        setIsAttemptingDeepLink(false);
      }
    };

    // Function to redirect to app store
    const redirectToAppStore = () => {
      if (!appOpened && !redirectAttempted) {
        redirectAttempted = true;
        setIsAttemptingDeepLink(false);
        window.location.href = appStoreUrl;
      }
    };

    // Set up event listeners to detect app opening
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeoutOnAppOpen();
      }
    };

    const handleBlur = () => {
      clearTimeoutOnAppOpen();
    };

    const handlePageHide = () => {
      clearTimeoutOnAppOpen();
    };

    const handleBeforeUnload = () => {
      clearTimeoutOnAppOpen();
    };

    // Additional detection for when app is already running
    const handleFocus = () => {
      // If we regain focus after a short delay, the app might be already running
      setTimeout(() => {
        if (document.hasFocus()) {
          clearTimeoutOnAppOpen();
        }
      }, 100);
    };

    // Add event listeners
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // For social media browsers, use a different approach
    if (isSocialMedia) {
      // Try to open the app
      window.location.href = appScheme;
      
      // Set a longer timeout for social media browsers
      timeout = setTimeout(redirectToAppStore, 4000);
    } else {
      // Standard approach for regular browsers
      timeout = setTimeout(redirectToAppStore, 2000);
      
      window.location.href = appScheme;
    }

    // Cleanup function
    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };

    // Return cleanup function for useEffect
    return cleanup;
  };

  useEffect(() => {
    // Only attempt deep linking on mobile devices
    if (!isMobileBrowser()) {
      return;
    }

    // Add a delay for social media browsers
    const delay = isInSocialMediaBrowser() ? 1500 : 500;
    
    const timeoutId = setTimeout(() => {
      const cleanup = attemptOpenApp();
      
      // Cleanup when component unmounts
      return cleanup;
    }, delay);

    return () => clearTimeout(timeoutId);
  }, []);

  // Show loading state while attempting to open app
  if (isAttemptingDeepLink) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        color: 'white',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '20px'
        }}>
          <h2>Eventle Uygulaması Açılıyor...</h2>
          <p>Eğer uygulama açılmazsa, otomatik olarak uygulama mağazasına yönlendirileceksiniz.</p>
          <div style={{
            marginTop: '20px',
            width: '50px',
            height: '50px',
            border: '3px solid #6535F3',
            borderTop: '3px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default DeepLinkHandler;
